import React, { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import { Link } from 'react-router-dom';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Avatar from 'components/base/Avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePause,
  faCircleStop,
  faPlay,
  faVolumeControlPhone,
  faStop
} from '@fortawesome/free-solid-svg-icons';
import { UilOutgoingCall, UilIncomingCall } from '@iconscout/react-unicons';
import Button from 'components/base/Button';
import { opencloseCampaign } from 'services/hvacallingmanagment.service';
import { numberFormat } from 'helpers/utils';

type HVACampaign = {
  id: number;
  bxpid: number;
  name: string;
  serviceid: number;
  campaigntype: number;
  servicename: string;
  brand: string;
  campaigntypedescription: string;
  campaigntypeoutbound: boolean;
  clients: number;
  status: number;
};

export const hvacampaignsTablecolumns: ColumnDef<HVACampaign>[] = [
  {
    accessorKey: 'servicename',
    header: 'Service',
    cell: ({ row: { original } }) => {
      const { servicename, brand } = original;
      const [imageSrc, setImageSrc] = useState(`/brands/${brand}.png`);

      const handleImageError = () => {
        setImageSrc('/brands/default.png');
      };

      return (
        <div className="d-flex align-items-center text-body-emphasis">
          {imageSrc && (
            <Avatar
              src={imageSrc}
              size="l"
              alt={servicename}
              onError={handleImageError}
            />
          )}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'pe-2 ps-0' },
      cellProps: { className: 'py-0 pe-2 ps-0 p-0 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'name',
    header: 'campaign',
    cell: ({ row: { original } }) => {
      const { id, bxpid, name, servicename, brand, campaigntypeoutbound } =
        original;
      const title = servicename + ' - ' + name;
      return (
        <Link
          // eslint-disable-next-line prettier/prettier
          to={`/actualcalling/campaign/${id}/${bxpid}/${campaigntypeoutbound ? 'outbound' : 'inbound'}/${title}/${brand}`}
          className="text-body"
        >
          {name}
        </Link>
      );
    },
    meta: {
      headerProps: { style: { width: '13%' }, className: 'px-1' },
      cellProps: { className: 'fw-bold px-1 white-space-nowrap' }
    }
  },
  {
    accessorKey: 'campaigntypeoutbound',
    header: 'Type',
    cell: ({ row: { original } }) => {
      const { campaigntypeoutbound } = original;
      return (
        <div className="d-flex mx-2 align-items-center text-body-quaternary">
          {campaigntypeoutbound ? (
            <UilOutgoingCall size="22" className="text-primary" />
          ) : (
            <UilIncomingCall size="22" className="text-success" />
          )}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  },
  {
    accessorKey: 'clients',
    header: 'Clients',
    cell: ({ row: { original } }) => {
      const { clients } = original;
      const formattedValue = clients !== null ? numberFormat(clients) : 'n/a';
      return (
        <div className="d-flex justify-content-center fw-bold text-body">
          {formattedValue}
        </div>
      );
    },
    meta: {
      headerProps: { style: { width: '5%' }, className: 'px-1 pe-4' },
      cellProps: { className: 'text-body-tertiary px-1 pe-4' }
    }
  },
  {
    header: 'Status',
    cell: ({ row: { original } }) => {
      const [status, setStatus] = useState(original.status);

      const handleButtonClick = async () => {
        if (status === null) return;

        let newStatus;
        let action;

        if (status === 0 || status === 2) {
          newStatus = 1;
          action = 'open';
        } else if (status === 1) {
          newStatus = 0;
          action = 'pause';
        } else {
          // En caso de un estado no reconocido, no hacemos nada
          return;
        }

        try {
          await opencloseCampaign(original.id, action);
          setStatus(newStatus);
        } catch (error) {
          alert('Failed to change campaign status: ' + error);
        }
      };

      const getStatusIcon = () => {
        if (status === null) return faCirclePause;
        switch (status) {
          case 0:
            return faCircleStop;
          case 1:
            return faVolumeControlPhone;
          case 2:
            return faCirclePause;
          default:
            return faCirclePause;
        }
      };

      const getStatusIconClass = () => {
        if (status === null) return 'text-body-quaternary';
        switch (status) {
          case 0:
            return 'text-warning';
          case 1:
            return 'text-primary';
          default:
            return 'text-body-quaternary';
        }
      };

      const getButtonText = () => {
        if (status === null)
          return (
            <>
              <FontAwesomeIcon icon={faPlay} /> Start
            </>
          );
        switch (status) {
          case 0:
            return (
              <>
                <FontAwesomeIcon icon={faPlay} /> Start
              </>
            );
          case 1:
            return (
              <>
                <FontAwesomeIcon icon={faStop} /> Stop
              </>
            );
          case 2:
            return (
              <>
                <FontAwesomeIcon icon={faPlay} /> Restart
              </>
            );
          default:
            return (
              <>
                <FontAwesomeIcon icon={faPlay} /> Start
              </>
            );
        }
      };

      return (
        <>
          <div className="d-flex mx-2 align-items-center text-body-quaternary">
            <span className="pe-6">
              <FontAwesomeIcon
                icon={getStatusIcon()}
                size="xl"
                shake={status === 1}
                className={getStatusIconClass()}
              />
            </span>
            <Button
              variant="primary"
              className="btn btn-primary"
              size="sm"
              onClick={handleButtonClick}
              disabled={status === null}
            >
              {getButtonText()}
            </Button>
          </div>
        </>
      );
    },
    meta: {
      headerProps: { style: { width: '63%' }, className: 'px-1' },
      cellProps: { className: 'text-body-tertiary px-1' }
    }
  }
];

export const HVACampaignsTable = () => {
  return (
    <div>
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
      />
      <AdvanceTableFooter pagination />
    </div>
  );
};
